import { defineStore } from 'pinia'
import { ref } from 'vue'
import { fetchMe, parseReason, signOut, type Me, type SignedOutReason } from '../api/session'

export type SessionState = 'checking' | 'in' | 'out' | 'error'

const REVIEWER_KEY = 'cf-chat.reviewer'

// sessionStorage: survives a reload of this tab, gone with the tab (an owner trying the reviewer sign-in in their own
// browser is not sent to /login in every later tab)
function readReviewer(): boolean {
  try {
    return sessionStorage.getItem(REVIEWER_KEY) === '1'
  } catch {
    return false // no storage (private window, tests)
  }
}

function rememberReviewer(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(REVIEWER_KEY, '1')
    else sessionStorage.removeItem(REVIEWER_KEY)
  } catch {
    /* no storage: the reviewer just opens /login again */
  }
}

/** Signed in or not. App.vue shows the inbox only when `state === 'in'`. */
export const useSession = defineStore('session', () => {
  const state = ref<SessionState>('checking')
  const me = ref<Me | null>(null)
  const reason = ref<SignedOutReason>(null)
  const error = ref<string | null>(null)
  const signingOut = ref(false)
  /** The last person signed in in this tab came in at /login (Meta reviewer): signed out, timed out or reloaded
   *  after that → back to /login, not the POS screen. Kept in sessionStorage; the next POS sign-in clears it. */
  const wasReviewer = ref(readReviewer())

  /** Read `?sso=` left by /auth/sso (App.vue removes it from the address bar once the router is ready). */
  function takeReasonFromUrl(): void {
    const r = parseReason(window.location.search)
    if (r) reason.value = r
  }

  async function check(): Promise<void> {
    state.value = 'checking'
    error.value = null
    try {
      const got = await fetchMe()
      me.value = got
      if (got) {
        wasReviewer.value = got.reviewer === true
        rememberReviewer(wasReviewer.value)
      }
      state.value = got ? 'in' : 'out'
    } catch {
      state.value = 'error'
      error.value = 'ต่อเซิร์ฟเวอร์แชทไม่ได้'
    }
  }

  /** A request came back 401 while the inbox was open (expired, signed out in another tab, disabled). */
  function lost(): void {
    if (state.value !== 'in') return
    me.value = null
    reason.value = 'session'
    state.value = 'out'
  }

  async function logout(): Promise<void> {
    signingOut.value = true
    try {
      await signOut()
      me.value = null
      reason.value = 'logged_out'
      state.value = 'out'
    } finally {
      signingOut.value = false
    }
  }

  return { state, me, reason, error, signingOut, wasReviewer, takeReasonFromUrl, check, lost, logout }
})
